import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Header as CarbonHeader,
  HeaderContainer,
  HeaderName,
  HeaderNavigation,
  HeaderMenuItem,
  HeaderMenu,
  HeaderGlobalBar,
  HeaderGlobalAction,
  HeaderMenuButton,
  SideNav,
  SideNavItems,
  SideNavLink,
  SideNavMenu,
  SideNavMenuItem,
  HeaderSideNavItems,
} from '@carbon/react';
import {
  Light,
  Asleep,
  Login,
  UserAvatar,
  ShoppingCart,
  Dashboard,
  Settings,
  Temperature,
  Wifi,
  Home,
  Location,
  NotificationNew,
  Logout,
  Analytics,
  Receipt,
  Map,
  Delivery,
  Purchase,
  ShoppingBag,
  Api,
  QrCode,
} from '@carbon/icons-react';
import PipelineIcon from './icons/PipelineIcon';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.webp';
import AlertsBell from './AlertsBell';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { cartCount } = useCart();
  const { user, isAuthenticated, signOut } = useAuth();
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const avatarMenuRef = useRef(null);

  // Detect mobile devices (including iPad)
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           (navigator.maxTouchPoints > 0 && /MacIntel/.test(navigator.platform));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(e.target)) {
        setAvatarMenuOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setAvatarMenuOpen(false);
    };
    if (avatarMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [avatarMenuOpen]);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Features', path: '/features' },
    { label: 'Sensors', path: '/sensors' },
    { label: 'Compliance', path: '/reports' },
    { label: 'Shop', path: '/shop' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Contact', path: '/contact' },
  ];

  const dashboardItems = [
    { label: 'Overview', path: '/dashboard', icon: Home },
    { label: 'Subscription', path: '/dashboard/subscription', icon: Purchase },
    { label: 'Billing History', path: '/billing', icon: Receipt },
    { label: 'Devices', path: '/dashboard/devices', icon: Temperature },
    { label: 'Gateways', path: '/dashboard/gateways', icon: Wifi },
    { label: 'Zones', path: '/dashboard/zones', icon: Location },
    { label: 'Shipments', path: '/dashboard/shipments', icon: Delivery },
    { label: 'Map', path: '/dashboard/maps', icon: Map },
    { label: 'Rules', path: '/dashboard/rules', icon: NotificationNew },
    { label: 'Intelligence', path: '/dashboard/intelligence', icon: Analytics },
    { label: 'Pipelines', path: '/dashboard/pipelines', icon: PipelineIcon },
    { label: 'Verify', path: '/dashboard/verify', icon: QrCode },
    { label: 'My Orders', path: '/dashboard/orders', icon: ShoppingBag },
    { label: 'Settings', path: '/dashboard/settings', icon: Settings },
    { label: 'API', path: '/dashboard/developers', icon: Api },
  ];

  const isDashboardItemActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isDashboard = location.pathname.startsWith('/dashboard');

  // Get user display name or email
  const getUserDisplayName = () => {
    if (!user) return '';
    
    // Try to get name from user_metadata (OAuth providers often set this)
    const metadata = user.user_metadata || {};
    const fullName = metadata.full_name || metadata.name;
    const firstName = metadata.first_name;
    const lastName = metadata.last_name;
    
    if (fullName) return fullName;
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    
    // Fallback to email
    return user.email || 'User';
  };

  const userDisplayName = getUserDisplayName();
  const userEmail = user?.email || '';

  return (
    <HeaderContainer
      render={({ isSideNavExpanded, onClickSideNavExpand }) => (
        <CarbonHeader aria-label="ChillVerify" data-testid="main-header">
          <HeaderMenuButton
            aria-label="Open menu"
            onClick={onClickSideNavExpand}
            isActive={isSideNavExpanded}
            data-testid="menu-button"
          />
          <HeaderName
            href="/"
            prefix=""
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            data-testid="header-logo"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <img 
                src={logo} 
                alt="ChillVerify Logo" 
                style={{ width: '24px', height: '24px', borderRadius: '4px' }}
              />
              <span className="app-brand" style={{ fontSize: '1rem' }}>ChillVerify</span>
            </div>
          </HeaderName>
          
          <HeaderNavigation aria-label="ChillVerify Navigation" data-testid="header-nav">
            {navItems.map((item) => (
              <HeaderMenuItem
                key={item.path}
                href={item.path}
                isCurrentPage={location.pathname === item.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(item.path);
                }}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </HeaderMenuItem>
            ))}
            {isAuthenticated && (
              <HeaderMenu
                aria-label="Dashboard"
                menuLinkName="Dashboard"
                data-testid="nav-dashboard-menu"
              >
                {dashboardItems.map((item) => {
                  const ItemIcon = item.icon;
                  return (
                  <HeaderMenuItem
                    key={item.path}
                    href={item.path}
                    isCurrentPage={isDashboardItemActive(item.path)}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(item.path);
                    }}
                    data-testid={`nav-dashboard-${item.label.toLowerCase()}`}
                  >
                    <span className="header-dashboard-menu-item">
                      <ItemIcon size={16} aria-hidden="true" />
                      {item.label}
                    </span>
                  </HeaderMenuItem>
                  );
                })}
              </HeaderMenu>
            )}
          </HeaderNavigation>

          <HeaderGlobalBar>
            {/* Alerts Bell - Only show when authenticated */}
            {isAuthenticated && <AlertsBell />}
            
            {/* Cart Icon */}
            <HeaderGlobalAction
              aria-label={`Cart (${cartCount} items)`}
              onClick={() => navigate('/cart')}
              data-testid="cart-icon"
              style={{ position: 'relative' }}
            >
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '6px',
                    right: '6px',
                    backgroundColor: '#da1e28',
                    color: '#ffffff',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    fontSize: '10px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  data-testid="cart-count"
                >
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </HeaderGlobalAction>
            <HeaderGlobalAction
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Light size={20} /> : <Asleep size={20} />}
            </HeaderGlobalAction>
            {isAuthenticated ? (
              <>
                <div ref={avatarMenuRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <HeaderGlobalAction
                    aria-label="User menu"
                    onClick={() => setAvatarMenuOpen(!avatarMenuOpen)}
                    data-testid="user-avatar-button"
                  >
                    <UserAvatar size={20} />
                  </HeaderGlobalAction>
                  {avatarMenuOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        minWidth: 220,
                        padding: '8px 0',
                        backgroundColor: theme === 'dark' ? '#262626' : '#ffffff',
                        border: `1px solid ${theme === 'dark' ? '#525252' : '#e0e0e0'}`,
                        borderRadius: '4px',
                        boxShadow: theme === 'dark'
                          ? '0 4px 16px rgba(0,0,0,0.5)'
                          : '0 4px 16px rgba(0,0,0,0.12)',
                        zIndex: 9100,
                        color: theme === 'dark' ? '#f4f4f4' : '#161616',
                      }}
                      data-testid="user-menu-dropdown"
                    >
                      <div
                        style={{
                          padding: '10px 16px 10px',
                          borderBottom: `1px solid ${theme === 'dark' ? '#393939' : '#e0e0e0'}`,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          wordBreak: 'break-all',
                        }}
                      >
                        {userDisplayName}
                        {userEmail && userDisplayName !== userEmail && (
                          <div style={{ fontWeight: 400, fontSize: '0.75rem', color: theme === 'dark' ? '#c6c6c6' : '#525252', marginTop: '2px' }}>
                            {userEmail}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setAvatarMenuOpen(false);
                          navigate('/dashboard');
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme === 'dark' ? '#353535' : '#f4f4f4'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: '10px 16px',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          color: theme === 'dark' ? '#f4f4f4' : '#161616',
                          textAlign: 'left',
                        }}
                        data-testid="user-menu-dashboard"
                      >
                        <Dashboard size={16} />
                        Dashboard
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAvatarMenuOpen(false);
                          navigate('/dashboard/settings');
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme === 'dark' ? '#353535' : '#f4f4f4'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: '10px 16px',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          color: theme === 'dark' ? '#f4f4f4' : '#161616',
                          textAlign: 'left',
                        }}
                        data-testid="user-menu-settings"
                      >
                        <Settings size={16} />
                        Settings
                      </button>
                      <div style={{ borderTop: `1px solid ${theme === 'dark' ? '#393939' : '#e0e0e0'}`, margin: '4px 0' }} />
                      <button
                        type="button"
                        onClick={async () => {
                          setAvatarMenuOpen(false);
                          await signOut();
                          navigate('/');
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme === 'dark' ? '#353535' : '#f4f4f4'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: '10px 16px',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          color: '#da1e28',
                          textAlign: 'left',
                        }}
                        data-testid="logout-button"
                      >
                        <Logout size={16} />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <HeaderGlobalAction
                  aria-label="Login"
                  onClick={() => navigate('/login')}
                  data-testid="login-button"
                >
                  <Login size={20} />
                </HeaderGlobalAction>
                <HeaderGlobalAction
                  aria-label="Sign Up"
                  onClick={() => navigate('/signup')}
                  tooltipAlignment="end"
                  data-testid="signup-button"
                >
                  <UserAvatar size={20} />
                </HeaderGlobalAction>
              </>
            )}
          </HeaderGlobalBar>

          <SideNav
            aria-label="Side navigation"
            expanded={isSideNavExpanded}
            isPersistent={false}
            data-testid="side-nav"
          >
            <SideNavItems>
              {/* Main Navigation */}
              <HeaderSideNavItems>
                {navItems.map((item) => (
                  <HeaderMenuItem
                    key={item.path}
                    href={item.path}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(item.path);
                      onClickSideNavExpand();
                    }}
                    data-testid={`sidenav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </HeaderMenuItem>
                ))}
              </HeaderSideNavItems>

              {/* Dashboard Section - Only show when authenticated */}
              {isAuthenticated && (
                <SideNavMenu
                  title="Dashboard"
                  renderIcon={Dashboard}
                  defaultExpanded={isDashboard}
                >
                  {dashboardItems.map((item) => (
                    <SideNavLink
                      key={item.path}
                      href={item.path}
                      renderIcon={item.icon}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(item.path);
                        onClickSideNavExpand();
                      }}
                      isActive={isDashboardItemActive(item.path)}
                    >
                      {item.label}
                    </SideNavLink>
                  ))}
                </SideNavMenu>
              )}

              {/* Cart Link */}
              <SideNavLink
                href="/cart"
                renderIcon={ShoppingCart}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/cart');
                  onClickSideNavExpand();
                }}
                isActive={location.pathname === '/cart'}
              >
                Cart {cartCount > 0 && `(${cartCount})`}
              </SideNavLink>

              {/* Auth Links - Only show when not authenticated */}
              {!isAuthenticated && (
                <>
                  <SideNavLink
                    href="/login"
                    renderIcon={Login}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/login');
                      onClickSideNavExpand();
                    }}
                    isActive={location.pathname === '/login'}
                  >
                    Login
                  </SideNavLink>
                  <SideNavLink
                    href="/signup"
                    renderIcon={UserAvatar}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/signup');
                      onClickSideNavExpand();
                    }}
                    isActive={location.pathname === '/signup'}
                  >
                    Sign Up
                  </SideNavLink>
                </>
              )}

              {/* Logout Link - Only show when authenticated */}
              {isAuthenticated && (
                <SideNavLink
                  href="#"
                  renderIcon={Logout}
                  onClick={async (e) => {
                    e.preventDefault();
                    await signOut();
                    navigate('/');
                    onClickSideNavExpand();
                  }}
                >
                  Logout
                </SideNavLink>
              )}

              {/* Theme Toggle in Side Nav for mobile convenience */}
              <SideNavLink
                href="#"
                renderIcon={theme === 'dark' ? Light : Asleep}
                onClick={(e) => {
                  e.preventDefault();
                  toggleTheme();
                }}
              >
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </SideNavLink>
            </SideNavItems>
          </SideNav>
        </CarbonHeader>
      )}
    />
  );
};

export default Header;
